// One conversation with the assistant, as the full Assistant page and the panel both use it. Your words go into the
// conversation at once (an optimistic message); the server's copy of your message takes over its place and React key,
// so the question is on screen exactly once, from the first frame to the last. A new conversation is created on the
// first message (create), so nothing is saved for a panel that was opened and closed without a word.
import { useCallback, useState } from 'react';
import { useIsMutating, useMutation, useQueryClient } from '@tanstack/react-query';
import type { Conversation, ConversationDetail, MessageResult } from '@dpw/shared';
import { api } from '../api';
import { useConversation } from '../lib/queries';

/** The React key of a message you sent stays the optimistic one's, so it is never replaced by a second copy. */
export const stableKey = new Map<string, string>();
let localSeq = 0;

export interface SayOptions {
  /** The words were spoken (voice mode): the reply is a few spoken sentences. */
  spoken?: boolean;
  /** The reply's words as they are written (voice mode reads them out sentence by sentence). */
  onText?: (delta: string) => void;
}

export function useChat(id: string | null | undefined, o: {
  /** Makes the conversation for the first message (with its scope). */
  create: () => Promise<Conversation>;
  /** Told the new conversation's id (the page navigates to it, the panel keeps it). */
  onCreated?: (id: string) => void;
  /** Sending in one place shows "thinking" in that place only. */
  key: string;
  /** The reply opened a page (the assistant panel follows it; the full page only shows the link). */
  onReply?: (r: MessageResult) => void;
}) {
  const qc = useQueryClient();
  const conv = useConversation(id ?? undefined);
  // The reply as it streams in, until the whole answer (with its cards) takes its place.
  const [liveReply, setLiveReply] = useState<string | null>(null);
  const sending = useMutation({
    mutationKey: ['send', o.key],
    mutationFn: async ({ text, conversationId, spoken, onText }: { text: string; conversationId: string | null; spoken?: boolean; onText?: (d: string) => void }): Promise<MessageResult> => {
      let cid = conversationId;
      if (!cid) {
        const c = await o.create();
        qc.setQueryData<ConversationDetail>(['conversation', c.id], { ...c, messages: [], actions: [], stagedFiles: [] });
        cid = c.id;
        o.onCreated?.(c.id);
      }
      const local = `local-${++localSeq}`;
      const key = ['conversation', cid];
      await qc.cancelQueries({ queryKey: key });
      qc.setQueryData<ConversationDetail>(key, c => c && { ...c, messages: [...c.messages, { id: local, role: 'user', content: text, meta: {}, createdAt: new Date().toISOString() }] });
      try {
        setLiveReply(null);
        const r = await api.assistant.sendStreaming(cid, text, d => { setLiveReply(x => (x ?? '') + d); onText?.(d); }, spoken ? { spoken: true } : {});
        setLiveReply(null);
        stableKey.set(r.userMessage.id, local);
        qc.setQueryData<ConversationDetail>(key, c => c && {
          ...c, ...r.conversation,
          messages: [...c.messages.filter(m => m.id !== local), r.userMessage, r.reply],
          actions: [...c.actions.filter(a => !r.actions.some(x => x.id === a.id) && !r.approved.some(x => x.id === a.id)), ...r.actions, ...r.approved],
        });
        void qc.invalidateQueries({ predicate: q => !(q.queryKey[0] === 'conversation' && q.queryKey[1] === cid) });
        o.onReply?.(r);
        return r;
      } catch (e) {
        setLiveReply(null);
        qc.setQueryData<ConversationDetail>(key, c => c && { ...c, messages: c.messages.filter(m => m.id !== local) });
        throw e;
      }
    },
  });
  const busy = useIsMutating({ mutationKey: ['send', o.key] }) > 0;
  const messages = conv.data?.messages ?? [];
  // Thinking: a message is on its way and the newest one in this conversation is still yours.
  const thinking = busy && (!id || messages.at(-1)?.role === 'user');

  const say = useCallback((t: string, so: SayOptions = {}) => {
    const v = t.trim();
    if (!v || busy) return Promise.resolve(null);
    return sending.mutateAsync({ text: v, conversationId: id ?? null, ...(so.spoken ? { spoken: true } : {}), ...(so.onText ? { onText: so.onText } : {}) }).catch(() => null);
  }, [id, busy, sending]);

  /** Files: the clip, or dropped on the conversation. A new conversation is created first. Returns its id. */
  const attach = useCallback(async (files: File[]): Promise<string | null> => {
    if (!files.length) return id ?? null;
    let cid = id ?? null;
    if (!cid) { const c = await o.create(); cid = c.id; o.onCreated?.(c.id); }
    for (const f of files) await api.assistant.attach(cid, f, f.name);
    await qc.invalidateQueries();
    return cid;
  }, [id, o, qc]);

  return {
    conv, messages, actions: conv.data?.actions ?? [], staged: conv.data?.stagedFiles ?? [], liveReply,
    say, attach, busy, thinking, error: sending.error, failedText: sending.isError ? sending.variables?.text ?? null : null, reset: sending.reset,
  };
}
